import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  Mail,
  Phone,
  Copy,
  Check,
  CheckCircle2,
  Sparkles,
  RefreshCw,
  Clock,
  Trash2,
  ExternalLink,
  MessageSquare,
  AlertCircle,
  ArrowLeft,
  Send,
  User,
  Filter,
} from "lucide-react";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import {
  useInquiries,
  useNewInquiries,
  useUpdateInquiryStatus,
  useDeleteInquiry,
} from "src/hooks/useInquiriesQuery";
import { useToast } from "src/hooks/useToast";
import { ROUTES } from "src/routes/RouteConfig";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/Dialog";

interface InquiriesProps {
  preview?: boolean;
  limit?: number;
}

export interface Inquiry {
  id: string;
  created_at: string;
  name: string;
  email: string;
  phone: string | null;
  inquiry_type: string;
  message: string;
  status: string;
}

// Light-mode Semantic Lifecycle Badges complying with rfLearn DESIGN.md
export const getStatusBadgeStyle = (status: string) => {
  switch (status?.toLowerCase()) {
    case "new":
      return "bg-rose-50 text-rose-700 border-rose-200";
    case "in_progress":
    case "in-progress":
      return "bg-amber-50 text-amber-800 border-amber-200";
    case "resolved":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "closed":
    case "archived":
      return "bg-slate-100 text-slate-700 border-slate-200";
    default:
      return "bg-purple-50 text-purple-700 border-purple-200";
  }
};

export const getStatusLabel = (status: string) => {
  switch (status?.toLowerCase()) {
    case "new":
      return "New Inquiry";
    case "in_progress":
    case "in-progress":
      return "In Progress";
    case "resolved":
      return "Resolved";
    case "closed":
    case "archived":
      return "Archived";
    default:
      return status ? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ") : "Inquiry";
  }
};

const formatRelativeTime = (isoString?: string | null): string => {
  if (!isoString) return "-";
  try {
    const diffMs = Date.now() - new Date(isoString).getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(isoString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "-";
  }
};

const formatFullDateTime = (isoString?: string | null): string => {
  if (!isoString) return "-";
  try {
    return new Date(isoString).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

const Inquiries: React.FC<InquiriesProps> = ({ preview = false, limit = 50 }) => {
  const navigate = useNavigate();
  const { setTitle, reset } = usePageHeaderStore();
  const { toast } = useToast();

  // Queries & Mutations
  const { data: rawInquiries = [], isLoading: loading, refetch, isRefetching } = useInquiries(limit);
  const { data: newInquiries = [] } = useNewInquiries();
  const updateStatusMutation = useUpdateInquiryStatus();
  const deleteInquiryMutation = useDeleteInquiry();

  // State
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Response composer template state
  const [selectedTemplate, setSelectedTemplate] = useState<string>("diagnostic");
  const [replyMessage, setReplyMessage] = useState<string>("");

  useEffect(() => {
    if (!preview) {
      setTitle("Inquiries Management");
      return () => reset();
    }
  }, [preview, setTitle, reset]);

  // Normalize inquiries list
  const inquiries = useMemo(() => {
    return (rawInquiries as Inquiry[]) || [];
  }, [rawInquiries]);

  // Distinct inquiry types for filtering
  const distinctTypes = useMemo(() => {
    const types = new Set<string>();
    inquiries.forEach((item) => {
      if (item.inquiry_type) {
        types.add(item.inquiry_type);
      }
    });
    return Array.from(types);
  }, [inquiries]);

  // Filtered inquiries
  const filteredInquiries = useMemo(() => {
    return inquiries.filter((inquiry) => {
      // Status filter
      if (statusFilter === "new") {
        if (inquiry.status !== "new") return false;
      } else if (statusFilter === "in_progress") {
        if (inquiry.status !== "in_progress" && inquiry.status !== "in-progress") return false;
      } else if (statusFilter === "resolved") {
        if (inquiry.status !== "resolved") return false;
      }

      // Type filter
      if (typeFilter !== "all" && inquiry.inquiry_type !== typeFilter) {
        return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const searchable = [
          inquiry.name,
          inquiry.email,
          inquiry.phone,
          inquiry.inquiry_type,
          inquiry.message,
          inquiry.id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!searchable.includes(q)) return false;
      }

      return true;
    });
  }, [inquiries, statusFilter, typeFilter, searchQuery]);

  // Ensure an inquiry is selected by default when list changes
  useEffect(() => {
    if (filteredInquiries.length > 0) {
      if (!selectedId || !filteredInquiries.some((item) => item.id === selectedId)) {
        setSelectedId(filteredInquiries[0].id);
      }
    } else {
      setSelectedId(null);
    }
  }, [filteredInquiries, selectedId]);

  // Active inquiry
  const activeInquiry = useMemo(() => {
    return inquiries.find((item) => item.id === selectedId) || null;
  }, [inquiries, selectedId]);

  // Metrics computation
  const metrics = useMemo(() => {
    const total = inquiries.length;
    const newCount = inquiries.filter((i) => i.status === "new").length;
    const inProgressCount = inquiries.filter((i) => i.status === "in_progress" || i.status === "in-progress").length;
    const resolvedCount = inquiries.filter((i) => i.status === "resolved").length;
    return { total, newCount, inProgressCount, resolvedCount };
  }, [inquiries]);

  // Response templates
  useEffect(() => {
    if (!activeInquiry) return;
    if (selectedTemplate === "diagnostic") {
      setReplyMessage(
        `Hi ${activeInquiry.name},\n\nThank you for reaching out to rfLearn regarding ${activeInquiry.inquiry_type || "our programs"}. We would love to schedule a diagnostic consultation and live 1:1 demonstration session with one of our specialized faculty mentors.\n\nPlease let us know what day and time slots work best for you this week.`
      );
    } else if (selectedTemplate === "curriculum") {
      setReplyMessage(
        `Hi ${activeInquiry.name},\n\nThank you for contacting rfLearn! We have prepared our comprehensive curriculum overview and milestone guide matching your inquiry. Our curriculum focuses on deep conceptual mastery, competition readiness, and tailored diagnostic checkpoints.\n\nWould you like us to walk through the course objectives on a brief call?`
      );
    } else if (selectedTemplate === "discovery") {
      setReplyMessage(
        `Hi ${activeInquiry.name},\n\nWe received your inquiry regarding "${activeInquiry.message.slice(0, 80)}...". Our academic advisors would be delighted to learn more about your student's learning goals.\n\nWhen would be a convenient time for a quick 10-minute discovery call?`
      );
    }
  }, [selectedTemplate, activeInquiry]);

  // Clipboard copy handler
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(label);
    toast({
      title: "Copied to Clipboard",
      description: `${label} copied successfully.`,
      type: "success",
    });
    setTimeout(() => {
      setCopiedKey((prev) => (prev === label ? null : prev));
    }, 2000);
  };

  // Status update handler
  const handleStatusUpdate = (id: string, status: string) => {
    updateStatusMutation.mutate({ id, status });
  };

  // Convert to Demo action
  const handleConvertToDemo = () => {
    if (!activeInquiry) return;
    toast({
      title: "Converting to Demo Lead",
      description: `Opening workstation with ${activeInquiry.name}'s contact details.`,
      type: "info",
    });
    navigate(ROUTES.ADMIN_DEMO_BOOKINGS.path, {
      state: {
        prefill: {
          parent_name: activeInquiry.name,
          email: activeInquiry.email,
          parent_phone: activeInquiry.phone,
          notes: `Converted from Inquiry #${activeInquiry.id.slice(0, 8)} (${activeInquiry.inquiry_type}): ${activeInquiry.message}`,
        },
      },
    });
  };

  // Delete confirmation
  const handleConfirmDelete = () => {
    if (!deleteConfirmId) return;
    deleteInquiryMutation.mutate(deleteConfirmId, {
      onSuccess: () => {
        setDeleteConfirmId(null);
        if (selectedId === deleteConfirmId) {
          setSelectedId(null);
        }
      },
    });
  };

  // ---------------------------------------------------------------------------
  // Dashboard Preview Mode (Backward Compatibility)
  // ---------------------------------------------------------------------------
  if (preview) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-900">Recent Inquiries</span>
            {metrics.newCount > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                {metrics.newCount} new
              </span>
            )}
          </div>
          <Link
            to={ROUTES.ADMIN_INQUIRIES.path}
            className="text-xs font-semibold text-purple-700 hover:text-purple-800"
          >
            View Workstation &rarr;
          </Link>
        </div>
        {loading ? (
          <div className="py-6 text-center text-xs text-slate-500">Loading inquiries...</div>
        ) : inquiries.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-500">No inquiries found</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {inquiries.slice(0, 4).map((inquiry) => (
              <div key={inquiry.id} className="py-2.5 flex items-center justify-between text-xs">
                <div>
                  <div className="font-semibold text-slate-900">{inquiry.name}</div>
                  <div className="text-slate-500 text-xs truncate max-w-xs">{inquiry.message}</div>
                </div>
                <div className="text-right shrink-0">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${getStatusBadgeStyle(inquiry.status)}`}>
                    {getStatusLabel(inquiry.status)}
                  </span>
                  <div className="text-xs text-slate-400 font-mono mt-0.5">{formatRelativeTime(inquiry.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Full Workstation View (Option A - Refined & Decluttered)
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-5">
      {/* Top Operational Metrics Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs">
          <div className="flex items-center justify-between text-slate-500 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Total Inquiries</span>
            <MessageSquare className="w-4 h-4 text-slate-400" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900">{metrics.total}</span>
            <span className="text-xs font-mono text-slate-400">lifetime</span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs">
          <div className="flex items-center justify-between text-slate-500 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Awaiting Triage</span>
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-rose-600">{metrics.newCount}</span>
            <span className="text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">
              Needs response
            </span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs">
          <div className="flex items-center justify-between text-slate-500 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">In Discussion</span>
            <Clock className="w-4 h-4 text-amber-500" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900">{metrics.inProgressCount}</span>
            <span className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
              Follow-up active
            </span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-2xs">
          <div className="flex items-center justify-between text-slate-500 mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Resolved</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-emerald-700">{metrics.resolvedCount}</span>
            <button
              onClick={() => refetch()}
              className="text-xs text-purple-700 hover:text-purple-800 font-medium flex items-center gap-1 ml-auto"
              title="Refresh Inquiries"
            >
              <RefreshCw className={`w-3 h-3 ${isRefetching ? "animate-spin text-purple-600" : ""}`} />
              Sync
            </button>
          </div>
        </div>
      </div>

      {/* Main Split Console */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-xs overflow-hidden grid grid-cols-1 lg:grid-cols-12 min-h-[640px]">
        {/* Left Side: Filterable Inquiry List (5 cols) */}
        <div className={`lg:col-span-5 border-r border-slate-200 flex flex-col bg-slate-50/40 ${activeInquiry ? "hidden lg:flex" : "flex"}`}>
          {/* Search and Filter Header */}
          <div className="p-3.5 border-b border-slate-200 bg-white space-y-2.5">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, keyword, or #ID..."
                className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white transition text-slate-900 placeholder-slate-400"
              />
            </div>

            {/* Quick Status Tabs */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs">
              <button
                onClick={() => setStatusFilter("all")}
                className={`px-2.5 py-1 rounded-md transition font-medium whitespace-nowrap ${
                  statusFilter === "all"
                    ? "bg-purple-50 text-purple-700 font-bold border border-purple-200"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                All ({inquiries.length})
              </button>
              <button
                onClick={() => setStatusFilter("new")}
                className={`px-2.5 py-1 rounded-md transition font-medium whitespace-nowrap flex items-center gap-1 ${
                  statusFilter === "new"
                    ? "bg-rose-50 text-rose-700 font-bold border border-rose-200"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                New
                {metrics.newCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-rose-200 text-rose-900 rounded-full font-mono text-xs font-bold">
                    {metrics.newCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setStatusFilter("in_progress")}
                className={`px-2.5 py-1 rounded-md transition font-medium whitespace-nowrap ${
                  statusFilter === "in_progress"
                    ? "bg-amber-50 text-amber-800 font-bold border border-amber-200"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                In Progress ({metrics.inProgressCount})
              </button>
              <button
                onClick={() => setStatusFilter("resolved")}
                className={`px-2.5 py-1 rounded-md transition font-medium whitespace-nowrap ${
                  statusFilter === "resolved"
                    ? "bg-emerald-50 text-emerald-700 font-bold border border-emerald-200"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                Resolved ({metrics.resolvedCount})
              </button>
            </div>

            {/* Type selector if multiple types exist */}
            {distinctTypes.length > 1 && (
              <div className="flex items-center gap-1.5 pt-1">
                <Filter className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full text-xs text-slate-700 bg-white border border-slate-200 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-600"
                >
                  <option value="all">All Inquiry Categories</option>
                  {distinctTypes.map((t) => (
                    <option key={t} value={t}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* List Entries */}
          <div className="overflow-y-auto flex-1 divide-y divide-slate-100 bg-white">
            {loading ? (
              <div className="p-8 text-center text-xs text-slate-500">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto text-purple-600 mb-2" />
                Loading inquiries workstation...
              </div>
            ) : filteredInquiries.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-500">
                <AlertCircle className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                No inquiries match your current filter.
              </div>
            ) : (
              filteredInquiries.map((inquiry) => {
                const isSelected = inquiry.id === selectedId;
                return (
                  <div
                    key={inquiry.id}
                    onClick={() => setSelectedId(inquiry.id)}
                    className={`p-3.5 cursor-pointer transition relative ${
                      isSelected
                        ? "bg-purple-50/80 ring-1 ring-inset ring-purple-200/80 shadow-2xs"
                        : "hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-bold text-xs text-slate-900 flex items-center gap-1.5 truncate">
                        {inquiry.name}
                        {inquiry.status === "new" && (
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" title="Awaiting response"></span>
                        )}
                      </span>
                      <span className="font-mono text-xs text-slate-400 shrink-0 ml-2">
                        {formatRelativeTime(inquiry.created_at)}
                      </span>
                    </div>

                    <div className="text-xs font-semibold text-slate-800 line-clamp-1 mb-1">
                      {inquiry.inquiry_type ? inquiry.inquiry_type.charAt(0).toUpperCase() + inquiry.inquiry_type.slice(1) : "General Inquiry"}
                    </div>

                    <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed mb-2">
                      "{inquiry.message}"
                    </p>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${getStatusBadgeStyle(inquiry.status)}`}>
                        {getStatusLabel(inquiry.status)}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs font-mono font-medium bg-slate-100 text-slate-600">
                        #{inquiry.id.slice(0, 8)}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* List Footer */}
          <div className="p-2.5 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500 px-4">
            <span>
              Showing {filteredInquiries.length} of {inquiries.length} records
            </span>
          </div>
        </div>

        {/* Right Side: Inspection & Immediate Resolution Console (7 cols) */}
        <div className={`lg:col-span-7 flex flex-col bg-white ${!activeInquiry ? "hidden lg:flex" : "flex"}`}>
          {activeInquiry ? (
            <div className="flex flex-col h-full">
              {/* Back to List button for mobile */}
              <div className="p-3 border-b border-slate-200 lg:hidden bg-slate-50 flex items-center">
                <button
                  onClick={() => setSelectedId(null)}
                  className="text-xs font-semibold text-purple-700 flex items-center gap-1"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Back to inquiries list
                </button>
              </div>

              {/* ----------------------------------------------------------------- */}
              {/* Dossier Header: Clean, Uncluttered, Refined (Option A) */}
              {/* ----------------------------------------------------------------- */}
              <div className="p-5 border-b border-slate-200 bg-[#faf9fe]">
                {/* Top Utility & Action Bar: Meta on Left, Clean Action Trio on Right */}
                <div className="flex items-center justify-between gap-3 mb-2.5">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap ${getStatusBadgeStyle(
                        activeInquiry.status
                      )}`}
                    >
                      {activeInquiry.status === "new" && (
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                      )}
                      {getStatusLabel(activeInquiry.status)}
                    </span>
                    <span className="text-xs font-mono font-medium text-slate-600 bg-white border border-slate-200 px-2 py-0.5 rounded shadow-2xs whitespace-nowrap">
                      #{activeInquiry.id.slice(0, 8)}
                    </span>
                    <span className="text-xs text-slate-400 font-medium whitespace-nowrap flex items-center gap-1">
                      &bull; {formatRelativeTime(activeInquiry.created_at)}
                    </span>
                  </div>

                  {/* Button Trio adhering to Section 4.2 of DESIGN.md with strict whitespace-nowrap */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() =>
                        handleCopy(
                          `rfLearn Inquiry: ${activeInquiry.name}\nEmail: ${activeInquiry.email}\nPhone: ${activeInquiry.phone || "N/A"}\nType: ${activeInquiry.inquiry_type}\nMessage: ${activeInquiry.message}`,
                          "Contact Info"
                        )
                      }
                      className="h-8 px-2.5 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-lg flex items-center gap-1 shadow-2xs transition whitespace-nowrap"
                      title="Copy Contact Details"
                    >
                      {copiedKey === "Contact Info" ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="w-3.5 h-3.5 text-slate-500" />
                      )}
                      <span>Copy</span>
                    </button>

                    <button
                      onClick={handleConvertToDemo}
                      className="h-8 px-3 text-xs font-semibold bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-lg flex items-center gap-1.5 shadow-2xs transition whitespace-nowrap"
                      title="Convert to Demo Lead in Workstation"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                      <span>Convert to Demo</span>
                    </button>

                    {activeInquiry.status !== "resolved" ? (
                      <button
                        onClick={() => handleStatusUpdate(activeInquiry.id, "resolved")}
                        className="h-8 px-3 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-1.5 shadow-2xs transition whitespace-nowrap"
                        title="Mark inquiry as resolved"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Resolve</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleStatusUpdate(activeInquiry.id, "new")}
                        className="h-8 px-2.5 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-lg flex items-center gap-1 shadow-2xs transition whitespace-nowrap"
                        title="Reopen as New Inquiry"
                      >
                        <RefreshCw className="w-3.5 h-3.5 text-slate-500" />
                        <span>Reopen</span>
                      </button>
                    )}

                    <button
                      onClick={() => setDeleteConfirmId(activeInquiry.id)}
                      className="h-8 px-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-200 rounded-lg transition shrink-0"
                      title="Delete Inquiry"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Subject Title given full breathing room */}
                <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight leading-snug break-words">
                  {activeInquiry.inquiry_type
                    ? activeInquiry.inquiry_type.charAt(0).toUpperCase() + activeInquiry.inquiry_type.slice(1) + " Inquiry"
                    : "General Student Inquiry"}
                </h2>

                {/* Identity & Direct Outreach Strip */}
                <div className="mt-3.5 pt-3 border-t border-purple-100/70 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 font-bold flex items-center justify-center text-xs shrink-0">
                      {activeInquiry.name
                        .split(" ")
                        .map((n) => n[0])
                        .slice(0, 2)
                        .join("")
                        .toUpperCase() || "U"}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-slate-900 truncate">{activeInquiry.name}</div>
                      <div className="text-xs text-slate-500 truncate">Prospect / Guardian</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-xs min-w-0">
                    <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                    <a
                      href={`mailto:${activeInquiry.email}?subject=${encodeURIComponent(
                        `rfLearn Inquiry: Re: ${activeInquiry.inquiry_type || "Tutoring"}`
                      )}`}
                      className="text-purple-700 hover:text-purple-800 font-medium truncate"
                      title={`Send email to ${activeInquiry.email}`}
                    >
                      {activeInquiry.email}
                    </a>
                  </div>

                  <div className="flex items-center gap-2 text-xs min-w-0">
                    <Phone className="w-4 h-4 text-slate-400 shrink-0" />
                    {activeInquiry.phone ? (
                      <a
                        href={`tel:${activeInquiry.phone}`}
                        className="font-mono text-slate-700 hover:text-purple-700 font-medium truncate"
                      >
                        {activeInquiry.phone}
                      </a>
                    ) : (
                      <span className="text-slate-400 italic">No phone provided</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Dossier Body Content */}
              <div className="p-6 flex-1 overflow-y-auto space-y-5">
                {/* Customer Message */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Customer Inquiry Message
                    </h3>
                    <span className="text-xs font-mono text-slate-400">
                      {formatFullDateTime(activeInquiry.created_at)}
                    </span>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-800 leading-relaxed whitespace-pre-wrap font-sans">
                    {activeInquiry.message}
                  </div>
                </div>

                {/* Quick Dispatch Composer */}
                <div className="border border-slate-200 rounded-xl p-4 bg-white shadow-2xs space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <span className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <Send className="w-3.5 h-3.5 text-purple-600" />
                      Quick Dispatch Outreach
                    </span>
                    <select
                      value={selectedTemplate}
                      onChange={(e) => setSelectedTemplate(e.target.value)}
                      className="text-xs font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1 focus:outline-none focus:ring-1 focus:ring-purple-600"
                    >
                      <option value="diagnostic">Template: Diagnostic Booking Invite</option>
                      <option value="curriculum">Template: Curriculum &amp; Syllabus Overview</option>
                      <option value="discovery">Template: Schedule Discovery Call</option>
                    </select>
                  </div>

                  <textarea
                    rows={4}
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    className="w-full p-3 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 focus:bg-white transition text-slate-900 placeholder-slate-400 font-sans"
                    placeholder="Personalize the response message..."
                  />

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pt-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Status transition:</span>
                      <select
                        value={activeInquiry.status}
                        onChange={(e) => handleStatusUpdate(activeInquiry.id, e.target.value)}
                        className="text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-600"
                      >
                        <option value="new">New</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleCopy(replyMessage, "Draft Reply")}
                        className="px-3 py-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-lg transition"
                      >
                        {copiedKey === "Draft Reply" ? "Copied Draft!" : "Copy Draft"}
                      </button>
                      <a
                        href={`mailto:${activeInquiry.email}?subject=${encodeURIComponent(
                          `rfLearn Inquiry: Re: ${activeInquiry.inquiry_type || "Tutoring"}`
                        )}&body=${encodeURIComponent(replyMessage)}`}
                        onClick={() => {
                          if (activeInquiry.status === "new") {
                            handleStatusUpdate(activeInquiry.id, "in_progress");
                          }
                        }}
                        className="px-4 py-1.5 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-lg shadow-2xs transition flex items-center gap-1.5"
                      >
                        <span>Open in Email &rarr;</span>
                      </a>
                    </div>
                  </div>
                </div>

                {/* Audit & Logistics Strip */}
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-500 space-y-1">
                  <div className="flex items-center justify-between">
                    <span>Database Record ID:</span>
                    <span className="font-mono text-slate-700">{activeInquiry.id}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Inquiry Category:</span>
                    <span className="font-semibold text-slate-700">{activeInquiry.inquiry_type || "General"}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-slate-400">
              <MessageSquare className="w-10 h-10 text-slate-300 mb-3" />
              <div className="text-sm font-bold text-slate-700">No Inquiry Selected</div>
              <p className="text-xs text-slate-400 mt-1 max-w-sm">
                Choose an inquiry from the left panel to review parent details, dispatch responses, or convert to a trial demo.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Inquiry Record</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this inquiry? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              onClick={() => setDeleteConfirmId(null)}
              className="px-3 py-1.5 text-xs font-semibold bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-3 py-1.5 text-xs font-bold bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition"
            >
              Permanently Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Inquiries;
